import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as EventoTecla,
  type PointerEvent as EventoPuntero,
  type ReactNode,
} from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { CURVA, desplegar } from './movimiento';
import { Icono, Numero } from './ui';
import { c, colorSector, miles } from '../theme';

/**
 * Los gráficos del WMS, dibujados a mano con SVG. Acá no entra ninguna
 * librería de gráficos: el resto del sistema —el plano de planta, la etiqueta,
 * la escena 3D— también dibuja su propio SVG, y una dependencia nueva sería
 * peso muerto que además rompe el estilo.
 *
 * ## De dónde sale la paleta
 *
 * Ningún color se inventa acá: los tres salen de `theme.ts`. El **orden** de
 * los slots no es decorativo, está validado para daltonismo (protanopía y
 * deuteranopía simuladas con Machado-Oliveira-Fernandes 2009, severidad 1.0,
 * ΔE en OKLab ×100, sobre panel blanco):
 *
 * - verde → azul → ámbar: peor par adyacente ΔE 13.8, piso 8. Pasa.
 * - ámbar pegado al verde: ΔE 4.8. **No pasa** — el ámbar nunca va al lado
 *   del verde en una barra apilada ni como serie contigua.
 *
 * Los tres superan 4.5:1 contra blanco (5.42 / 5.39 / 4.71), así que además
 * aguantan texto blanco encima cuando la etiqueta va adentro del relleno.
 *
 * ## Regla que se cumple en todos los componentes de acá
 *
 * El color nunca es el único portador del dato: siempre hay leyenda con el
 * número al lado, o tabla, o las dos cosas. El texto usa tokens de texto
 * (`c.texto`, `c.texto2`, `c.texto3`), nunca el color de la serie.
 */
export const PALETA = {
  /** Lo que entra al depósito. El verde corporativo, `c.verde`. */
  entra: c.verde,
  /** Lo que sale. El azul del vocabulario de sectores, `colorSector('azul')`. */
  sale: colorSector('azul'),
  /** Lo pendiente, lo que falta hacer. El ámbar de aviso, `c.ambar`. */
  falta: c.ambar,
} as const;

// -------------------------------------------------------------- utilidades

/**
 * El ancho real del contenedor, en píxeles. Los SVG de acá usan
 * `viewBox="0 0 ancho alto"` con `width: 100%`, así que el sistema de
 * coordenadas coincide 1:1 con la pantalla y el texto nunca sale escalado
 * (un `viewBox` fijo achica la tipografía a la mitad en un celular).
 */
function useAncho(inicial = 640) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [ancho, setAncho] = useState(inicial);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entradas) => {
      const w = entradas[0]?.contentRect.width ?? 0;
      if (w > 0) setAncho(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, ancho] as const;
}

const MULTIPLOS = [0.1, 0.2, 0.25, 0.5, 1, 2, 2.5, 5, 10];

/**
 * Techo y paso del eje vertical. Se busca el paso **más chico** de la escala
 * 1-2-2,5-5 que deje como mucho `maxTicks` divisiones: así el techo queda
 * pegado al dato en lugar de sobrarle media pantalla (con 2.100 piezas el eje
 * llega a 2.500, no a 4.000). Los pasos fraccionarios se descartan porque
 * todo lo que se grafica acá son piezas y movimientos, que son enteros.
 */
function escalaLinda(max: number, maxTicks: number): { tope: number; paso: number } {
  if (max <= 0) return { tope: 0, paso: 0 };
  const magnitud = Math.pow(10, Math.floor(Math.log10(max)));
  for (const m of MULTIPLOS) {
    const paso = m * magnitud;
    if (paso < 1 || !Number.isInteger(paso)) continue;
    if (Math.ceil(max / paso) <= maxTicks) return { tope: Math.ceil(max / paso) * paso, paso };
  }
  return { tope: max, paso: max };
}

/** Ancho aproximado de un texto de eje o de etiqueta, en píxeles. */
const anchoTexto = (texto: string, tamano: number) => texto.length * tamano * 0.58;

const ejeTexto: CSSProperties = {
  fontSize: 12,
  fill: c.texto3,
  fontVariantNumeric: 'tabular-nums',
};

// ---------------------------------------------------------------- leyenda

/**
 * La leyenda. Siempre está cuando hay dos o más series, y siempre lleva el
 * número al lado del nombre: es la etiqueta directa que no se pisa con
 * ninguna otra, y es lo que hace que el color no sea el único dato.
 */
export function Leyenda({
  items,
  forma = 'linea',
}: {
  items: { nombre: string; color: string; valor?: ReactNode }[];
  forma?: 'linea' | 'caja';
}) {
  return (
    <ul
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 18px',
        margin: 0,
        padding: 0,
        listStyle: 'none',
      }}
    >
      {items.map((it) => (
        <li key={it.nombre} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span
            aria-hidden="true"
            style={
              forma === 'linea'
                ? { width: 16, height: 3, borderRadius: 999, background: it.color, flex: '0 0 16px' }
                : { width: 11, height: 11, borderRadius: 3, background: it.color, flex: '0 0 11px' }
            }
          />
          <span style={{ fontSize: 12.5, color: c.texto2 }}>{it.nombre}</span>
          {it.valor !== undefined && (
            <span style={{ fontSize: 12.5, fontWeight: 600, color: c.texto, fontVariantNumeric: 'tabular-nums' }}>
              {it.valor}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

// ------------------------------------------------------------------ líneas

export type SerieLinea = { nombre: string; color: string; valores: number[] };

const PAD = { arriba: 14, abajo: 28, izq: 56, der: 16 };

/**
 * Dos o más series a lo largo del tiempo, una línea cada una sobre **un solo
 * eje**. Trae mira vertical con globo (mouse y flechas del teclado) y la
 * tabla con todos los valores debajo, plegada: el globo mejora la lectura,
 * nunca es la única forma de llegar al número.
 */
export function Lineas({
  etiquetas,
  series,
  soloEnTabla = [],
  alto = 240,
  descripcion,
  encabezadoTiempo = 'Día',
  textoTabla = 'Ver los 14 días en tabla',
}: {
  etiquetas: string[];
  series: SerieLinea[];
  /** Columnas que van a la tabla pero no al dibujo: un segundo eje sería mentira. */
  soloEnTabla?: { nombre: string; valores: number[] }[];
  alto?: number;
  /** Alternativa en texto del dibujo, para lector de pantalla. */
  descripcion: string;
  encabezadoTiempo?: string;
  textoTabla?: string;
}) {
  const [ref, ancho] = useAncho();
  const [foco, setFoco] = useState<number | null>(null);
  const [verTabla, setVerTabla] = useState(false);

  const n = etiquetas.length;
  const plotW = Math.max(ancho - PAD.izq - PAD.der, 10);
  const plotH = Math.max(alto - PAD.arriba - PAD.abajo, 10);

  const maximo = Math.max(0, ...series.flatMap((s) => s.valores));
  const { tope, paso } = escalaLinda(maximo, 5);
  const escala = tope || 1;

  const px = (i: number) => PAD.izq + (n <= 1 ? plotW / 2 : (plotW * i) / (n - 1));
  const py = (v: number) => PAD.arriba + plotH - (plotH * Math.max(v, 0)) / escala;

  const ticks =
    tope === 0 || paso === 0
      ? [0]
      : Array.from({ length: Math.round(tope / paso) + 1 }, (_, k) => k * paso);

  // Con catorce días y una tarjeta angosta las fechas se pisan: se muestra
  // una de cada tantas, siempre incluyendo la última, que es la que importa.
  const anchoEtiqueta = Math.max(...etiquetas.map((e) => anchoTexto(e, 12)), 1) + 12;
  const pasoX = Math.max(1, Math.ceil((n * anchoEtiqueta) / plotW));

  function indiceDesdeEvento(e: EventoPuntero<SVGSVGElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    if (r.width === 0 || n === 0) return null;
    const x = ((e.clientX - r.left) * ancho) / r.width;
    const rel = (x - PAD.izq) / plotW;
    return Math.min(n - 1, Math.max(0, Math.round(rel * Math.max(n - 1, 1))));
  }

  function alTeclear(e: EventoTecla<SVGSVGElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setFoco((f) => {
      const base = f ?? n - 1;
      return Math.min(n - 1, Math.max(0, base + (e.key === 'ArrowRight' ? 1 : -1)));
    });
  }

  const columnas = [...series.map((s) => ({ nombre: s.nombre, valores: s.valores })), ...soloEnTabla];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Leyenda
        items={series.map((s) => ({
          nombre: s.nombre,
          color: s.color,
          valor: <Numero valor={s.valores[n - 1] ?? 0} />,
        }))}
      />
      <div style={{ marginTop: 2, fontSize: 12, color: c.texto3 }}>
        El número de la leyenda es el del último día del período.
      </div>

      <svg
        role="img"
        aria-label={descripcion}
        tabIndex={0}
        width="100%"
        height={alto}
        viewBox={`0 0 ${ancho} ${alto}`}
        style={{ display: 'block', marginTop: 8, touchAction: 'pan-y' }}
        onPointerMove={(e) => setFoco(indiceDesdeEvento(e))}
        onPointerLeave={() => setFoco(null)}
        onKeyDown={alTeclear}
        onBlur={() => setFoco(null)}
      >
        {/* Grilla: líneas finas, enteras, un escalón más clara que el panel. */}
        {ticks.map((t) => {
          const y = Math.round(py(t)) + 0.5;
          return (
            <g key={t}>
              <line x1={PAD.izq} y1={y} x2={ancho - PAD.der} y2={y} stroke={c.borde} strokeWidth={1} />
              <text x={PAD.izq - 8} y={y + 4} textAnchor="end" style={ejeTexto}>
                {miles(Math.round(t))}
              </text>
            </g>
          );
        })}

        {/* Fechas del eje horizontal. */}
        {etiquetas.map((et, i) => {
          if (i % pasoX !== (n - 1) % pasoX) return null;
          const anclaje = i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle';
          return (
            <text key={et + i} x={px(i)} y={alto - 9} textAnchor={anclaje} style={ejeTexto}>
              {et}
            </text>
          );
        })}

        {/* Mira vertical: el lector apunta a un día, no a una línea de 2 px. */}
        {foco !== null && (
          <line
            x1={px(foco)}
            y1={PAD.arriba}
            x2={px(foco)}
            y2={PAD.arriba + plotH}
            stroke={c.bordeTenue}
            strokeWidth={1}
          />
        )}

        {series.map((s) => (
          // La línea se dibuja sola al aparecer, una vez: sólo cambia la
          // longitud trazada, los puntos y la escala no se tocan.
          <motion.polyline
            key={s.nombre}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.8, ease: CURVA }}
            fill="none"
            stroke={s.color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            points={s.valores.map((v, i) => `${px(i)},${py(v)}`).join(' ')}
          />
        ))}

        {/* Punto final de cada serie y, si hay foco, el punto del día mirado.
            El anillo blanco de 2 px los mantiene legibles cuando se cruzan. */}
        {n > 0 &&
          series.flatMap((s) => {
            const indices = foco !== null && foco !== n - 1 ? [n - 1, foco] : [n - 1];
            return indices.map((i) => (
              <circle
                key={`${s.nombre}-${i}`}
                cx={px(i)}
                cy={py(s.valores[i] ?? 0)}
                r={4}
                fill={s.color}
                stroke={c.panel}
                strokeWidth={2}
              />
            ));
          })}
      </svg>

      {foco !== null && (
        <GloboSerie
          x={Math.min(Math.max(px(foco), 82), Math.max(ancho - 82, 82))}
          titulo={etiquetas[foco] ?? ''}
          filas={columnas.map((col, i) => ({
            nombre: col.nombre,
            valor: miles(col.valores[foco] ?? 0),
            color: series[i]?.color,
          }))}
        />
      )}

      <button
        type="button"
        aria-expanded={verTabla}
        onClick={() => setVerTabla((v) => !v)}
        style={{
          marginTop: 12,
          padding: 0,
          border: 'none',
          background: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 12.5,
          color: c.texto2,
          cursor: 'pointer',
        }}
      >
        <motion.span animate={{ rotate: verTabla ? 90 : 0 }} transition={{ duration: 0.3, ease: CURVA }} style={{ display: 'flex' }}>
          <Icono nombre="chevron_right" size={18} />
        </motion.span>
        {textoTabla}
      </button>
      <AnimatePresence initial={false}>
        {verTabla && (
          <motion.div {...desplegar}>
        <div className="wms-scroll-x" style={{ marginTop: 10 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
            <thead>
              <tr>
                <th style={celdaCabecera('left')}>{encabezadoTiempo}</th>
                {columnas.map((col) => (
                  <th key={col.nombre} style={celdaCabecera('right')}>
                    {col.nombre}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {etiquetas.map((et, i) => (
                <tr key={et + i}>
                  <td style={celda('left')}>{et}</td>
                  {columnas.map((col) => (
                    <td key={col.nombre} style={celda('right')}>
                      {miles(col.valores[i] ?? 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const celdaCabecera = (alineado: 'left' | 'right'): CSSProperties => ({
  padding: '7px 10px',
  textAlign: alineado,
  borderBottom: `1px solid ${c.borde}`,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: c.texto3,
  whiteSpace: 'nowrap',
});

const celda = (alineado: 'left' | 'right'): CSSProperties => ({
  padding: '7px 10px',
  textAlign: alineado,
  borderBottom: `1px solid ${c.borde}`,
  color: c.texto,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
});

/** El globo de la mira: el número manda, el nombre de la serie acompaña. */
function GloboSerie({
  x,
  titulo,
  filas,
}: {
  x: number;
  titulo: string;
  filas: { nombre: string; valor: string; color?: string }[];
}) {
  return (
    <div
      role="status"
      style={{
        position: 'absolute',
        top: 44,
        left: x,
        transform: 'translateX(-50%)',
        pointerEvents: 'none',
        background: c.panel,
        border: `1px solid ${c.bordeFuerte}`,
        borderRadius: 12,
        boxShadow: '0 6px 20px rgba(26,26,26,.12)',
        padding: '9px 12px',
        minWidth: 150,
        zIndex: 2,
      }}
    >
      <div style={{ fontSize: 12, color: c.texto3, marginBottom: 5 }}>{titulo}</div>
      {filas.map((f) => (
        <div key={f.nombre} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
          <span
            aria-hidden="true"
            style={{
              width: 12,
              height: 3,
              borderRadius: 999,
              flex: '0 0 12px',
              background: f.color ?? c.texto4,
            }}
          />
          <span style={{ fontSize: 13.5, fontWeight: 600, color: c.texto, fontVariantNumeric: 'tabular-nums' }}>
            {f.valor}
          </span>
          <span style={{ fontSize: 12, color: c.texto2 }}>{f.nombre}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------- barra apilada

export type ParteApilada = { nombre: string; valor: number; color: string };

/**
 * Parte-sobre-el-todo en una sola barra horizontal: la proporción se lee de
 * un vistazo y los números quedan en la leyenda. Entre tramo y tramo hay
 * 2 px de panel, que es lo que los separa; ninguno lleva borde dibujado.
 */
export function BarraApilada({
  partes,
  alto = 30,
  descripcion,
  vacio = 'Sin datos para repartir.',
}: {
  partes: ParteApilada[];
  alto?: number;
  descripcion: string;
  vacio?: string;
}) {
  const [ref, ancho] = useAncho();
  const recorte = useId().replace(/:/g, '');

  const total = partes.reduce((s, p) => s + Math.max(p.valor, 0), 0);
  const pct = (v: number) => (total > 0 ? (v / total) * 100 : 0);

  const HUECO = 2;
  const visibles = partes.filter((p) => p.valor > 0);
  const util = Math.max(ancho - HUECO * Math.max(visibles.length - 1, 0), 1);

  let cursor = 0;
  const tramos = partes.map((p) => {
    const w = p.valor > 0 ? (util * p.valor) / total : 0;
    const x = cursor;
    if (w > 0) cursor += w + HUECO;
    return { ...p, x, w };
  });

  return (
    <div ref={ref}>
      {total === 0 ? (
        <div
          role="img"
          aria-label={vacio}
          style={{
            height: alto,
            borderRadius: 4,
            background: c.fondo,
            border: `1px dashed ${c.bordeFuerte}`,
            display: 'grid',
            placeItems: 'center',
            fontSize: 12.5,
            color: c.texto3,
          }}
        >
          {vacio}
        </div>
      ) : (
        <svg
          role="img"
          aria-label={descripcion}
          width="100%"
          height={alto}
          viewBox={`0 0 ${ancho} ${alto}`}
          style={{ display: 'block' }}
        >
          <defs>
            <clipPath id={recorte}>
              <rect x={0} y={0} width={ancho} height={alto} rx={4} ry={4} />
            </clipPath>
          </defs>
          <g clipPath={`url(#${recorte})`}>
            {tramos.map((t, i) =>
              t.w <= 0 ? null : (
                <g key={t.nombre}>
                  {/* Cada tramo crece desde su arranque, uno después del otro. */}
                  <motion.rect
                    y={0}
                    height={alto}
                    fill={t.color}
                    initial={{ x: t.x, width: 0 }}
                    animate={{ x: t.x, width: t.w }}
                    transition={{ duration: 0.7, ease: CURVA, delay: i * 0.08 }}
                  >
                    <title>{`${t.nombre}: ${miles(t.valor)} (${pct(t.valor).toFixed(0)}%)`}</title>
                  </motion.rect>
                  {/* La etiqueta va adentro sólo si entra entera con aire a
                      los costados; si no entra, la leyenda la lleva igual. */}
                  {t.w > anchoTexto(`${pct(t.valor).toFixed(0)}%`, 12) + 18 && (
                    <motion.text
                      x={t.x + t.w / 2}
                      y={alto / 2 + 4}
                      textAnchor="middle"
                      style={{ fontSize: 12, fontWeight: 600, fill: '#fff' }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.45 + i * 0.08 }}
                    >
                      {`${pct(t.valor).toFixed(0)}%`}
                    </motion.text>
                  )}
                </g>
              ),
            )}
          </g>
        </svg>
      )}

      <div style={{ marginTop: 12 }}>
        <Leyenda
          forma="caja"
          items={partes.map((p) => ({
            nombre: p.nombre,
            color: p.color,
            valor:
              total > 0 ? (
                <>
                  <Numero valor={p.valor} /> · {pct(p.valor).toFixed(0)}%
                </>
              ) : (
                miles(p.valor)
              ),
          }))}
        />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- medidor

/**
 * Una razón contra su límite: ocupación de posiciones. El relleno lleva la
 * gravedad —verde hasta 75 %, ámbar hasta 90 %, rojo arriba de 90 %, porque
 * un depósito lleno es un depósito sin dónde guardar— y la pista es el tono
 * suave del mismo color, así el estado se lee en toda la barra. El
 * porcentaje va escrito al lado: el color nunca es el único dato.
 */
export function Medidor({
  valor,
  total,
  rotulo,
  pie,
}: {
  valor: number;
  total: number;
  rotulo: string;
  pie?: ReactNode;
}) {
  const porcentaje = total > 0 ? Math.min(100, Math.round((valor / total) * 100)) : 0;
  const gravedad =
    total === 0 || porcentaje <= 75
      ? { relleno: c.verde, pista: c.verdeSuave, borde: c.verdeBorde }
      : porcentaje <= 90
        ? { relleno: c.ambar, pista: c.ambarSuave, borde: c.ambar }
        : { relleno: c.rojo, pista: c.rojoSuave, borde: c.rojo };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, color: c.texto2, flex: 1, minWidth: 120 }}>{rotulo}</span>
        <span
          style={{
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: total === 0 ? c.texto3 : gravedad.relleno,
          }}
        >
          {total === 0 ? '—' : <Numero valor={porcentaje} formato={(n) => `${n}%`} />}
        </span>
      </div>

      <div
        role="img"
        aria-label={
          total === 0
            ? `${rotulo}: todavía no hay posiciones cargadas.`
            : `${rotulo}: ${miles(valor)} de ${miles(total)} posiciones ocupadas, ${porcentaje} por ciento.`
        }
        style={{
          marginTop: 8,
          height: 12,
          borderRadius: 999,
          background: total === 0 ? c.fondo : gravedad.pista,
          border: `1px solid ${total === 0 ? c.borde : gravedad.borde}`,
          overflow: 'hidden',
        }}
      >
        <motion.div
          initial={{ width: 0, backgroundColor: gravedad.relleno }}
          animate={{ width: `${porcentaje}%`, backgroundColor: gravedad.relleno }}
          transition={{ duration: 0.7, ease: CURVA }}
          style={{
            height: '100%',
            borderRadius: '0 999px 999px 0',
          }}
        />
      </div>

      <div style={{ marginTop: 8, fontSize: 12.5, color: c.texto2 }}>
        {total === 0 ? 'Todavía no hay posiciones cargadas.' : pie}
      </div>
    </div>
  );
}

// -------------------------------------------------------------- barra de fila

/**
 * La barra que va adentro de una fila de tabla, al lado del número. Es un
 * rectángulo y nada más, así que se dibuja con un `div`: un SVG por fila
 * necesitaría medir cada fila para no deformar las esquinas redondeadas.
 * El extremo del dato va redondeado y el arranque queda al ras de la línea
 * de base, como en el resto de los gráficos.
 */
export function BarraFila({
  valor,
  maximo,
  color,
  descripcion,
  retraso = 0,
}: {
  valor: number;
  maximo: number;
  color: string;
  descripcion: string;
  /** Segundos de espera antes de crecer: para escalonar las barras de una lista. */
  retraso?: number;
}) {
  // Un valor distinto de cero nunca se dibuja como nada: se le deja un
  // mínimo visible, y el cero sí queda en cero.
  const bruto = maximo > 0 ? (Math.max(valor, 0) / maximo) * 100 : 0;
  const porcentaje = valor > 0 ? Math.max(bruto, 3) : 0;

  return (
    <span
      role="img"
      aria-label={descripcion}
      title={descripcion}
      style={{
        display: 'block',
        width: '100%',
        height: 8,
        borderRadius: '0 4px 4px 0',
        background: c.fondo,
      }}
    >
      <motion.span
        initial={{ width: 0 }}
        animate={{ width: `${porcentaje}%` }}
        transition={{ duration: 0.6, ease: CURVA, delay: Math.min(retraso, 0.5) }}
        style={{
          display: 'block',
          height: '100%',
          borderRadius: '0 4px 4px 0',
          background: color,
        }}
      />
    </span>
  );
}

